import { Avatar, Spin } from "antd";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom/cjs/react-router-dom.min";
import { Col, Container, Row, Table, Card, CardBody } from "reactstrap";
import BackButton from "../../components/button/BackButton";
import useCategoryDetails from "./customHooks/useCategoryDetails";
import { FiGrid, FiBox, FiInfo } from "react-icons/fi";
import CountButton from "../../components/button/CountButton";
import TableSkeleton from "../../components/skeleton/TableSkeleton";

const CategoryDetails = () => {
    const { state } = useLocation();
    const history = useHistory();

    const { all_subCategories } = useSelector((state) => state.redux);

    const { details, subCategories, products, loading } =
        useCategoryDetails(state, all_subCategories);

    return (
        <div className="settings-container">
            <Container>
                {/* Header Section */}
                <div className="d-flex justify-content-between align-items-center mb-4 px-2">
                    <div className="d-flex align-items-center gap-3">
                        <div className="bg-primary-soft p-3 rounded-circle shadow-sm">
                            <FiGrid size={24} className="text-primary" />
                        </div>
                        <div>
                            <h4 className="fw-bold text-dark mb-0">{details?.categoryName}</h4>
                            <p className="text-muted small mb-0">Management & Overview</p>
                        </div>
                    </div>
                    <BackButton handleClick={() => history.goBack()} />
                </div>

                <Row className="gy-4">
                    {/* Basic Info Card */}
                    <Col lg={12}>
                        <Card className="settings-card shadow-sm border-0">
                            <CardBody className="p-4">
                                <h5 className="section-title mb-4">
                                    <FiInfo className="me-2" /> Basic Information
                                </h5>
                                <Row className="align-items-center">
                                    <Col md={3} className="text-center mb-3 mb-md-0">
                                        <div className="avatar-container shadow-sm border p-2 bg-white rounded-3" style={{ width: '150px', height: '150px' }}>
                                            <img
                                                src={details?.categoryImage ?? details?.categoryImageURL}
                                                alt="Category"
                                                className="img-fluid h-100 object-fit-contain"
                                            />
                                        </div>
                                    </Col>
                                    <Col md={9}>
                                        <Row className="gy-3">
                                            <Col sm={6}>
                                                <div className="user-badge bg-secondary-soft text-dark px-3 py-2">
                                                    <span className="text-muted me-2">ID:</span>
                                                    <span className="fw-bold">{details?.id}</span>
                                                </div>
                                            </Col>
                                            <Col sm={6}>
                                                <div className="user-badge bg-primary-soft text-primary px-3 py-2">
                                                    <span className="text-muted me-2">Type:</span>
                                                    <span className="fw-bold">{details?.categoryType}</span>
                                                </div>
                                            </Col>
                                            <Col sm={12}>
                                                <div className="p-3 bg-light rounded-3 border-start border-4 border-primary">
                                                    <span className="text-muted d-block small mb-1">Status Summary</span>
                                                    <p className="mb-0 fw-500">
                                                        This category currently manages <strong className="text-primary">{subCategories?.length ?? 0}</strong> sub-categories and <strong className="text-primary">{products?.length ?? 0}</strong> active products.
                                                    </p>
                                                </div>
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>
                    </Col>

                    {/* Sub Categories Table */}
                    <Col lg={12}>
                        <Card className="settings-card shadow-sm border-0">
                            <CardBody className="p-4">
                                <CountButton title="Sub Categories" count={subCategories?.length} />
                                {subCategories?.length > 0 ? (
                                    <div className="modern-table-container p-0">
                                        <Table responsive className="modern-table mb-0">
                                            <thead>
                                                <tr>
                                                    <th className="text-center">#</th>
                                                    <th className="text-center">Image</th>
                                                    <th className="text-center">Sub Category Title</th>
                                                    <th className="text-center">ID</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {subCategories.map((sub, index) => (
                                                    <tr key={index}>
                                                        <td className="text-center text-muted">{index + 1}</td>
                                                        <td className="text-center">
                                                            <Avatar
                                                                src={sub?.subCategoryImage ?? sub?.subCategoryImageURL}
                                                                size={45}
                                                                className="shadow-sm border"
                                                            />
                                                        </td>
                                                        <td className="text-center fw-bold">{sub?.subCategoryName}</td>
                                                        <td className="text-center">
                                                            <span className="user-badge bg-secondary-soft">#{sub?.id}</span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                ) : (
                                    <div className="text-center py-5 text-muted bg-light rounded-3">
                                        <FiGrid size={40} className="mb-2 opacity-25" />
                                        <p className="mb-0">No sub-categories assigned to this category.</p>
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </Col>

                    {/* Products Table */}
                    <Col lg={12}>
                        <Card className="settings-card shadow-sm border-0 mb-5">
                            <CardBody className="p-4">
                                <CountButton title="Products" count={products?.length} />
                                {loading ? (
                                    <div className="mt-3">
                                        <TableSkeleton columns={6} />
                                    </div>
                                ) : products?.length > 0 ? (
                                    <div className="modern-table-container p-0">
                                        <Table responsive className="modern-table mb-0">
                                            <thead>
                                                <tr>
                                                    <th className="text-center">Product</th>
                                                    <th className="text-center">SKU / ID</th>
                                                    <th className="text-center">Title</th>
                                                    <th className="text-center">Inventory</th>
                                                    <th className="text-center">Brand</th>
                                                    <th className="text-right">Price</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {products.map((p, index) => (
                                                    <tr key={index}>
                                                        <td className="text-center">
                                                            <Avatar src={p?.imageUrl} size={40} className="shadow-sm border" />
                                                        </td>
                                                        <td className="text-center small">
                                                            <div className="fw-bold">{p?.sku}</div>
                                                            <div className="text-muted">{p?.id}</div>
                                                        </td>
                                                        <td className="fw-bold small" style={{ maxWidth: '250px', whiteSpace: 'normal' }}>
                                                            {p?.name}
                                                        </td>
                                                        <td className="text-center">
                                                            <span className={`user-badge ${p.stc_quantity > 0 ? 'bg-success-soft text-success' : 'bg-danger-soft text-danger'}`}>
                                                                {p?.stc_quantity} Units
                                                            </span>
                                                        </td>
                                                        <td className="text-center fw-500">{p?.mfg_name}</td>
                                                        <td className="text-right fw-bold text-primary">${p?.price}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                ) : (
                                    <div className="text-center py-5 text-muted bg-light rounded-3">
                                        <FiBox size={40} className="mb-2 opacity-25" />
                                        <p className="mb-0">No active products found in this category.</p>
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default CategoryDetails;

